import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Route, Routes, useNavigate, useLocation, Navigate } from 'react-router-dom';
import ContentTabs from './ContentTabs';
import DailyTrainings from './sections/DailyTrainings';
import BonusVideos from './sections/BonusVideos';
import Instruction from './sections/Instruction';
import Presentation from './sections/Presentation';
import BeforeAfter from './sections/BeforeAfter';
import FreeVideo from './sections/FreeVideo';
import styles from './ContentList.module.scss';
import Recordings from '../recordings/Recordings';

/**
 * Компонент списка контента
 * 
 * Отвечает за:
 * - Управление вкладками разделов контента
 * - Маршрутизацию между разделами контента
 * - Отслеживание текущего активного раздела
 * - Отображение соответствующего компонента раздела
 * 
 * Разделы контента:
 * - trainings - ежедневные тренировки
 * - bonus - бонусные видео
 * - instruction - инструкции
 * - presentation - презентация
 * - before-after - результаты до/после
 */
const ContentList: React.FC = () => {
    const [currentTab, setCurrentTab] = useState(0);
    const [dailyTrainingsCount, setDailyTrainingsCount] = useState(0);
    const location = useLocation();
    const navigate = useNavigate();

    // Массив путей для маршрутизации - обернут в useMemo
    const paths = useMemo(() => ['trainings', 'bonus', 'instruction', 'presentation', 'before-after', 'free', 'recordings'], []);

    /**
     * Эффект для синхронизации текущей вкладки с URL
     * При изменении URL устанавливает соответствующую активную вкладку
     */
    useEffect(() => {
        const currentPath = location.pathname.split('/').pop() || '';
        const tabIndex = paths.indexOf(currentPath);
        if (tabIndex !== -1) {
            setCurrentTab(tabIndex);
        }
    }, [location, paths]);

    /**
     * Обработчик смены вкладки
     * При выборе вкладки обновляет URL и текущий активный индекс
     * 
     * Мемоизирован для предотвращения ненужных перерендеров
     */
    const handleTabChange = useCallback((newValue: number) => {
        navigate(`/content/${paths[newValue]}`);
    }, [navigate, paths]);

    /**
     * Обработчик изменения количества тренировок
     * 
     * Мемоизирован для предотвращения ненужных перерендеров
     */
    const handleTrainingsCountChange = useCallback((count: number) => {
        setDailyTrainingsCount(count);
    }, []);

    return (
        <div className={styles.contentContainer}>
            <ContentTabs
                currentTab={currentTab}
                onTabChange={handleTabChange}
                dailyTrainingsCount={dailyTrainingsCount}
            />
            <div className={styles.contentSection}>
                <Routes>
                    <Route index element={<Navigate to="trainings" replace />} />
                    <Route
                        path="trainings/*"
                        element={<DailyTrainings onCountChange={handleTrainingsCountChange} />}
                    />
                    <Route path="bonus/*" element={<BonusVideos />} />
                    <Route path="instruction/*" element={<Instruction />} />
                    <Route path="presentation/*" element={<Presentation />} />
                    <Route path="before-after/*" element={<BeforeAfter />} />
                    <Route path="free/*" element={<FreeVideo />} />
                    <Route path="recordings" element={<Recordings />} />
                </Routes>
            </div>
        </div>
    );
};

export default ContentList;